<script lang="ts">
  import { onDestroy, untrack } from "svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import {
    updateDailyBriefingSettings,
    type DailyBriefingSettings,
  } from "../../../../../../../../api/kehrblatt/daily-briefing"

  const {
    organizationId,
    settings,
    canManage,
    pageSaving = false,
    onSaved,
    onStateChange,
  } = $props<{
    organizationId: string
    settings: DailyBriefingSettings
    canManage: boolean
    pageSaving?: boolean
    onSaved: (value: DailyBriefingSettings) => void
    onStateChange: (state: { dirty: boolean; busy: boolean; valid: boolean }) => void
  }>()
  const readDraft = (value: DailyBriefingSettings) => ({
    enabled: String(value.enabled),
    sendTime: value.send_time.slice(0, 5),
    leadDays: String(value.planning_lead_days),
  })
  let lastSaved = $state(untrack(() => readDraft(settings)))
  let draft = $state(untrack(() => ({ ...lastSaved })))
  let saving = $state(false)
  let message = $state("")
  let error = $state("")
  const dirty = $derived(
    draft.enabled !== lastSaved.enabled ||
      draft.sendTime !== lastSaved.sendTime ||
      draft.leadDays !== lastSaved.leadDays,
  )
  const disabled = $derived(!canManage || saving || pageSaving)
  let active = true
  $effect(() => onStateChange({ dirty, busy: saving, valid: Boolean(draft.sendTime) }))
  onDestroy(() => {
    active = false
  })

  /** Save this editor's draft for the page action; failures retain all unsaved values. */
  export async function saveChanges(): Promise<boolean> {
    if (!dirty) return true

    if (!canManage || saving || !draft.sendTime) return false

    saving = true
    error = message = ""
    try {
      const result = await updateDailyBriefingSettings(organizationId, {
        enabled: draft.enabled === "true",
        send_time: draft.sendTime,
        planning_lead_days: Number(draft.leadDays) as DailyBriefingSettings["planning_lead_days"],
      })

      if (!active) return false

      lastSaved = readDraft(result)
      draft = { ...lastSaved }
      onSaved(result)
      message = "Einstellungen gespeichert."

      return true
    } catch {
      if (active) error = "Speichern fehlgeschlagen. Ihre Änderungen bleiben erhalten."

      return false
    } finally {
      if (active) saving = false
    }
  }
</script>

<section class="settings-stack" aria-label="Automatischer Versand">
  <SectionTitle
    title="Automatischer Versand"
    brightBackground={true}
    small={true}
    style="margin-bottom: 1.2rem;"
  />
  <div class="box-content form">
    <div class="settings-row">
      <div class="field-block">
        <Input
          type="select"
          placeholder="Morgenübersicht automatisch versenden"
          bind:value={draft.enabled}
          options={[
            { value: "true", label: "Aktiviert" },
            { value: "false", label: "Deaktiviert" },
          ]}
          clearable={false}
          brightBackground={true}
          {disabled}
          tooltip="Montags bis freitags für bezahlte Produktivorganisationen. Ohne relevante Inhalte entfällt die E-Mail."
        />
      </div>
      <div class="field-block">
        <Input
          type="time"
          placeholder={`Versandzeit (${settings.timezone})`}
          bind:value={draft.sendTime}
          required={true}
          brightBackground={true}
          {disabled}
        />
      </div>
      <div class="field-block">
        <Input
          type="select"
          placeholder="Gewünschter Planungsvorlauf"
          bind:value={draft.leadDays}
          options={[7, 14, 21, 28].map((value) => ({
            value: String(value),
            label: `${value} Tage`,
          }))}
          clearable={false}
          brightBackground={true}
          {disabled}
          tooltip="Mindestens Terminbrief-Vorlauf plus zwei Tage Bearbeitungspuffer. Der Planungsausblick betrachtet die nächsten 42 Tage."
        />
      </div>
    </div>
  </div>
  {#if error}<p role="alert">{error}</p>{/if}
  {#if message}<p role="status">{message}</p>{/if}
</section>
<style>
  .settings-stack,
  .box-content {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    min-width: 0;
  }
  .settings-row {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 13rem), 1fr));
    gap: 0.9rem;
  }
  .field-block {
    min-width: 0;
  }
  p {
    margin: 0;
    color: var(--text-invers-100);
  }
  [role="alert"] {
    border-left: 3px solid var(--red-300);
    padding: 0.6rem 0.8rem;
  }
</style>
